import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import { forwardRef, MouseEvent, useCallback, useState } from 'react';
import { HexAlphaColorPicker, HexColorPicker } from 'react-colorful';
import { Button } from '@/wrappers/Button';
import { TextField, ITextFieldProps } from '../TextField';

export type IColorPickerProps = Omit<ITextFieldProps, 'onChange'> & {
    label?: string;
    value?: string;
    disableAlpha?: boolean;
    onChange?: (value: string) => void;
};

const StyledAdornment = styled('div', {
    shouldForwardProp: (props) => props !== 'background'
})<{ background?: string }>(
    ({ theme, background }) => `
        width: ${theme.spacing(3)};
        min-width: ${theme.spacing(3)};
        height: ${theme.spacing(3)};
        min-height: ${theme.spacing(3)};
        margin: ${theme.spacing(1)};
        padding: 0;
        background-color: ${background};
        border-radius: ${theme.shape.borderRadius}px;
    `
);
const StyledButton = styled(Button, {
    shouldForwardProp: (props) => props !== 'background'
})<{ background?: string }>(
    ({ theme, background }) => `
        width: ${theme.spacing(3)};
        min-width: ${theme.spacing(3)};
        height: ${theme.spacing(3)};
        min-height: ${theme.spacing(3)};
        margin: ${theme.spacing(1)} 0;
        padding: 0;
        background-color: ${background};
        background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(135deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(135deg, transparent 75%, #ccc 75%);
        background-size: 8px 8px;
        background-position: 0 0, 4px 0, 4px -4px, 0px 4px;
        background-clip: content-box;
        border-radius: ${theme.shape.borderRadius}px;
        border: 1px solid rgba(0,0,0,0.15);
    `
);
const StyledButtonContent = styled('div', {
    shouldForwardProp: (props) => props !== 'background'
})<{ background?: string }>(
    ({ theme, background }) => `
        width: 100%;
        height: 100%;
        background-color: ${background};
        border-radius: ${theme.shape.borderRadius}px;
    `
);

export const ColorPicker = forwardRef<HTMLDivElement, IColorPickerProps>(function InnerColorPicker(
    { disableAlpha, label, value = '#000000', readOnly, onChange, ...rest }: IColorPickerProps,
    ref
) {
    const [element, setElement] = useState<HTMLButtonElement | null>(null);
    const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => setElement(event.currentTarget), []);
    const handleClose = useCallback(() => setElement(null), []);
    const handleChange = useCallback(
        (data: string) => {
            if (onChange) {
                onChange(data);
            }
        },
        [onChange]
    );
    const color = value ? `#${value.replace('#', '')}` : '';

    return (
        <>
            <TextField
                {...rest}
                ref={ref}
                label={label}
                value={color}
                slotProps={{
                    input: {
                        endAdornment: readOnly ? (
                            <StyledAdornment background={color} />
                        ) : (
                            <StyledButton
                                name={`${rest.name}-selectButton`}
                                disableElevation
                                background={color}
                                variant="contained"
                                onClick={handleClick}
                            >
                                <StyledButtonContent background={color} />
                            </StyledButton>
                        )
                    }
                }}
            />
            <Popover
                open={!!element}
                anchorEl={element}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                sx={{ top: '5px' }}
                onClose={handleClose}
            >
                <Box data-testid={`${label}-colorPickerPopoverBox`} sx={{ padding: 1.5 }}>
                    {disableAlpha ? (
                        <HexColorPicker color={color} onChange={handleChange} />
                    ) : (
                        <HexAlphaColorPicker color={color} onChange={handleChange} />
                    )}
                </Box>
            </Popover>
        </>
    );
});
